import { useState, useMemo, useEffect } from "react";
import { useSupplier } from "../../../hooks/query";
import { useProject } from "../../../hooks";

interface UseSupplierCompareInitProps {
    selectedValues: Record<string, any>;
    type?: string;
}

export function useSupplierCompareInit({ selectedValues, type = "Manufacturer" }: UseSupplierCompareInitProps) {
    const { project } = useProject();
    const { useSupplierComparison, useList } = useSupplier();
    const comparisonSvc = useSupplierComparison();

    const { data: suppliers = [], isLoading: isLoadingSuppliers } = useList(type);

    const [hasInitializedSuppliers, setHasInitializedSuppliers] = useState(false);
    const [selectedSupplierNames, setSelectedSupplierNames] = useState<string[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [comparisonResults, setComparisonResults] = useState<any[]>([]);
    const [isComparing, setIsComparing] = useState(false);

    const { itemSupplierSelections } = useMemo(() => {
        const mapping: Record<string, string> = {};

        Object.values(selectedValues).forEach((item: any) => {
            if (item?.multiLine && Array.isArray(item.lines)) {
                item.lines.forEach((line: any) => {
                    if (line?.supplierName) {
                        const key = line.manufacturerProductId || line.value;
                        if (key) mapping[key] = line.supplierName;
                    }
                });
                return;
            }

            if (item && item.supplierName) {
                const key = item.manufacturerProductId || item.value;
                if (key) mapping[key] = item.supplierName;
            }
        });
        return {
            itemSupplierSelections: mapping,
        };
    }, [selectedValues]);

    useEffect(() => {
        const uniqueSuppliers = Array.from(new Set(Object.values(itemSupplierSelections)));
        if (!hasInitializedSuppliers && uniqueSuppliers.length > 0) {
            setSelectedSupplierNames(uniqueSuppliers);
            setHasInitializedSuppliers(true);
        }
    }, [itemSupplierSelections, hasInitializedSuppliers]);

    const handleCompare = async () => {
        if (!project?.id || selectedSupplierNames.length === 0) return;
        setOpenModal(true);
        setIsComparing(true);
        try {
            const results = await comparisonSvc.mutateAsync({
                SupplierNames: selectedSupplierNames,
                ProjectId: project.id,
                Category: type === "LABOR" ? "Labor" : "Materials",
            });
            setComparisonResults(results);
        } catch (error) {
            console.error("Comparison Error:", error);
            setOpenModal(false);
        } finally {
            setIsComparing(false);
        }
    };

    return {
        suppliers,
        selectedSupplierNames,
        setSelectedSupplierNames,
        openModal,
        setOpenModal,
        comparisonResults,
        isComparing,
        itemSupplierSelections,
        handleCompare,
        isLoadingSuppliers,
    };
}
